import { accountActions, alertActions, profilesActions } from '@actions';
import MoreMenu from '@components/MoreMenu';
import StatusChip from '@components/StatusChip';
import { Table, getPaginationLabels } from '@components/Table';
import Tooltip from '@components/Tooltip';
import { Typography } from '@components/Typography';
import { ProfileName, ProfileType } from '@constants/integrations';
import { SYNC_BADGE, SYNC_TYPE, SyncDiff, ListingDirectoryPlatformId } from '@constants/profiles';
import { RoutePath } from '@constants/routes';
import DirectoriesAddOnModal from '@features/DirectoriesAddOnModal';
import { handleApiError } from '@helpers/api';
import { isLegacyPaidPlan } from '@helpers/billing';
import { getCompanyFullAddress } from '@helpers/companies';
import { I18N_DATE_FORMAT } from '@helpers/dates';
import { formatPhoneNumber } from '@helpers/phone';
import { countResolvableDiffs } from '@helpers/profileResolve';
import {
    findProfileById,
    getBadgeColors,
    getBadgeLabel,
    getProfileSyncBadge,
    isScannerProfile,
} from '@helpers/profileSync';
import { usePermissions, useTranslation } from '@hooks';
import LanguageIcon from '@mui/icons-material/Language';
import LinkIcon from '@mui/icons-material/Link';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { styled } from '@mui/material/styles';
import TableContainer from '@mui/material/TableContainer';
import { companyService, profilesService } from '@services';
import { OpenProfileIcon, SuccessIcon, UpgradeIcon } from 'assets/images/icons';
import {
    CoverManagerIcon,
    FacebookIcon,
    GoogleIcon,
    IFoodIcon,
    ListingDirectoriesIcon,
} from 'assets/images/integrations';
import { format, formatDistanceToNowStrict } from 'date-fns';
import VerificationModal from '@pages/LocationsManagePage/VerificationModal';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

// Scanner rows are "publish-locked" while the location's ListingDirectories integration is
// in the publish handover window (Pending or Connected) — the Publish menu item must be hidden
// regardless of the row's visible status. A Synced row would still trigger AlreadyConnected on
// the backend, and a non-Synced row already renders as IN_PROGRESS via the backend override.
const isScannerPublishLocked = profile => isScannerProfile(profile) && profile?.isLocationPublishing === true;

const canShowScannerAction = ({ profile, isLegacyPlan, canManageSettings, allowDirectories }) => {
    if (!allowDirectories) return false;
    if (isScannerPublishLocked(profile)) return false;
    if (profile?.syncStatus === SYNC_TYPE.SYNCED) return false;
    return isLegacyPlan ? profile?.canEdit === true : canManageSettings === true;
};

const hasMenuItems = ({ profile, isLegacyPlan, canManageSettings, allowDirectories }) => {
    if (profile?.businessUrl) return true;
    if (isScannerProfile(profile)) {
        return canShowScannerAction({ profile, isLegacyPlan, canManageSettings, allowDirectories });
    }
    if (profile?.syncStatus === SYNC_TYPE.DISCONNECTED) return canManageSettings === true;
    if (!profile?.canEdit) return false;
    const isListingDirectory = profile?.platform === ListingDirectoryPlatformId;
    if (!isListingDirectory) return true; // enable/disable sync available
    return profile?.syncStatus === SYNC_TYPE.ACTION_REQUIRED || profile?.syncStatus === SYNC_TYPE.SYNCED_WITH_CONFLICTS;
};

const IconWrapper = styled('span')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    '& svg': {
        height: 18,
        width: 'auto',
    },
});

const StyledSuccessIcon = styled(SuccessIcon)({
    height: 18,
    width: 18,
});

// Wrapper to left-align the chip — TableCellStyled defaults cells to align="center".
const ChipCell = styled('div')({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
});

const getTableHeader = t => {
    return [
        {
            key: 0,
            width: 140,
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('common.platform')}
                </Typography>
            ),
        },
        {
            key: 1,
            width: 220,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.location')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ProfilesPage.profileInformation')}
                </Typography>
            ),
        },
        {
            key: 3,
            width: 160,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.status')}
                </Typography>
            ),
        },
        {
            key: 4,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ProfilesPage.details')}
                </Typography>
            ),
        },
        {
            key: 5,
            width: 160,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ProfilesPage.lastChecked')}
                </Typography>
            ),
        },
        { key: 6, width: 56, component: <Typography align="left" variant="subtitle2" /> },
    ];
};

const LocationIntegrationIcon = ({ type }) => {
    switch (type) {
        case ProfileName.GoogleOfficial:
            return <GoogleIcon />;
        case ProfileName.FacebookOfficial:
            return <FacebookIcon />;
        case ProfileName.iFood:
            return <IFoodIcon />;
        case ProfileName.CoverManager:
            return <CoverManagerIcon />;
        default:
            return null;
    }
};

const CONFLICT_COLOR = 'var(--error-main)';

const hasFieldConflict = (profile, field) => Array.isArray(profile?.syncDiff) && profile.syncDiff.includes(field);

const getTableBody = (
    data,
    locations,
    language,
    anchor,
    handleOpenTop,
    handleCloseTop,
    getMenuForRow,
    isLegacyPlan,
    canManageSettings,
    allowDirectories,
    t,
) => {
    const EMPTY_CELL = '--';

    // BE serializes nullable DateTime without Z; without this, JS parses as local
    // and renders "in 3 hours" for a UTC value 3 hours ago. Localized FE workaround —
    // the public API contract pins the no-Z format so we don't widen the BE converter.
    const ensureUtcSuffix = value => {
        if (typeof value !== 'string') return value;
        return /Z$|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`;
    };

    const formatLastSynced = value => {
        if (!value) return null;
        const parsed = new Date(ensureUtcSuffix(value));
        if (Number.isNaN(parsed.getTime())) return null;
        // Guard against any code path that leaks a default DateTime (e.g. 0001-01-01) — would
        // otherwise render as "2025 years ago".
        if (parsed.getUTCFullYear() < 2000) return null;
        const locale = I18N_DATE_FORMAT[language];
        return {
            relative: formatDistanceToNowStrict(parsed, { addSuffix: true, locale }),
            absolute: format(parsed, 'dd MMM yyyy, HH:mm', { locale }),
        };
    };

    const getDetailsText = (profile, badge, platformName) => {
        switch (badge) {
            case SYNC_BADGE.CONFLICT: {
                const count = countResolvableDiffs(profile?.syncDiff);
                const key = isScannerProfile(profile)
                    ? 'ProfilesPage.detailsConflictScanner'
                    : 'ProfilesPage.detailsConflictIntegration';
                return t(key, { count, platform: platformName });
            }
            case SYNC_BADGE.NOT_FOUND:
                return t(
                    isScannerProfile(profile) && !isLegacyPlan
                        ? 'ProfilesPage.detailsNotFoundScanner'
                        : 'ProfilesPage.detailsNotFound',
                    { platform: platformName },
                );
            case SYNC_BADGE.NOT_VERIFIED:
                return t('ProfilesPage.detailsNotVerified', { platform: platformName });
            case SYNC_BADGE.DISCONNECTED:
                return t('ProfilesPage.detailsDisconnected', { platform: platformName });
            case SYNC_BADGE.SYNCED:
                if (profile.syncStatus === SYNC_TYPE.SYNCED_WITH_CONFLICTS) {
                    const count = countResolvableDiffs(profile?.syncDiff);
                    return t('ProfilesPage.detailsSyncedWithConflicts', { count });
                }
                return t('ProfilesPage.detailsSynced');
            case SYNC_BADGE.DISABLED:
                return t('ProfilesPage.detailsDisabled');
            default:
                return null;
        }
    };

    return data.map(profile => {
        const isListingDirectory = profile.platform === ListingDirectoryPlatformId;
        const fullAddress = profile.syncData?.hasBusinessAddress ? getCompanyFullAddress(profile.syncData) : '--';
        const companyName = locations?.find(location => location.companyId === profile.locationId)?.internalName;
        const name = isListingDirectory ? profile.listingDirectoryName : ProfileType[profile.platform];
        const badge = getProfileSyncBadge(profile);
        const badgeColors = getBadgeColors(badge);
        const detailsHeadline = getDetailsText(profile, badge, name);
        const lastSynced = formatLastSynced(profile.lastSyncedAt);
        return [
            {
                component: (
                    <Typography variant="body2" align="left">
                        <IconWrapper>
                            {isListingDirectory ? (
                                profile.listingDirectoryLogo ? (
                                    <img src={profile.listingDirectoryLogo} alt={name} height={18} width={18} />
                                ) : (
                                    <ListingDirectoriesIcon />
                                )
                            ) : (
                                <LocationIntegrationIcon type={profile.platform} />
                            )}
                            {name}
                        </IconWrapper>
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {companyName}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="subtitle2" align="left" component="div">
                        <Typography
                            variant="subtitle2"
                            component="div"
                            sx={{
                                color: hasFieldConflict(profile, SyncDiff.LocationName) ? CONFLICT_COLOR : undefined,
                            }}
                        >
                            {profile.syncData?.locationName}
                        </Typography>
                        <Typography
                            variant="body2"
                            component="div"
                            sx={{
                                color: hasFieldConflict(profile, SyncDiff.Address)
                                    ? CONFLICT_COLOR
                                    : 'var(--text-primary)',
                                marginTop: profile.syncData?.locationName ? '4px' : 0,
                            }}
                        >
                            {fullAddress || '--'}
                        </Typography>
                        <Typography
                            variant="body2"
                            component="div"
                            sx={{
                                color: hasFieldConflict(profile, SyncDiff.PhoneNumber)
                                    ? CONFLICT_COLOR
                                    : 'var(--text-primary)',
                            }}
                        >
                            {formatPhoneNumber(profile.syncData?.phoneNumber)}
                        </Typography>
                    </Typography>
                ),
            },
            {
                component: (
                    <ChipCell>
                        <StatusChip bg={badgeColors.bg} fg={badgeColors.fg} label={getBadgeLabel(badge, t)} />
                    </ChipCell>
                ),
            },
            {
                component: (
                    <Typography variant="caption" align="left" component="div" sx={{ color: 'var(--text-primary)' }}>
                        {detailsHeadline ?? EMPTY_CELL}
                    </Typography>
                ),
            },
            {
                component: lastSynced ? (
                    <Tooltip title={lastSynced.absolute} placement="top" enterTouchDelay={0}>
                        <span tabIndex={0} style={{ whiteSpace: 'nowrap' }}>
                            <Typography
                                variant="body2"
                                align="left"
                                component="span"
                                sx={{ color: 'var(--text-primary)' }}
                            >
                                {lastSynced.relative}
                            </Typography>
                        </span>
                    </Tooltip>
                ) : (
                    <Typography
                        variant="body2"
                        align="left"
                        sx={{ color: 'var(--text-primary)', whiteSpace: 'nowrap' }}
                    >
                        {EMPTY_CELL}
                    </Typography>
                ),
            },
            ...(hasMenuItems({ profile, isLegacyPlan, canManageSettings, allowDirectories })
                ? [
                      {
                          component: (
                              <MoreMenu
                                  id={profile.profileId}
                                  anchor={anchor}
                                  handleOpenEl={handleOpenTop}
                                  handleCloseEl={handleCloseTop}
                                  menu={anchor.id === profile.profileId ? getMenuForRow(profile.profileId) : []}
                              />
                          ),
                      },
                  ]
                : [{ component: <div /> }]),
        ];
    });
};

const ProfilesTable = ({
    locations,
    rowsPerPage,
    page,
    onPageChange,
    onRowsPerPageChange,
    onClickResolve,
    onRefetch,
}) => {
    const [anchor, setAnchor] = useState({ el: null, id: null });
    const [verificationModal, setVerificationModal] = useState({ open: false, profileId: null, googleAccountId: null });
    const [addOnModal, setAddOnModal] = useState({ open: false, location: null });

    const { t } = useTranslation();
    const { data, loading, totalCount, pages } = useSelector(state => state.profiles);
    const account = useSelector(state => state.account?.account);
    const language = useSelector(state => state.auth.user?.language);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { hasFullSettings } = usePermissions();
    const canManageSettings = hasFullSettings();

    const isLegacyPlan = useMemo(() => isLegacyPaidPlan(account), [account]);
    const allowDirectories = account?.allowListingDirectories === true;

    const handleOpenTop = (event, id) => {
        event.stopPropagation();
        setAnchor({ id, el: event.currentTarget });
    };

    const handleCloseTop = () => setAnchor(prev => ({ ...prev, el: null }));

    const onToggleEnableSync = async e => {
        e.stopPropagation();
        handleCloseTop();
        const profile = findProfileById(data, anchor.id);
        if (!profile) return;

        try {
            const updatedProfile = profile.syncEnabled
                ? await profilesService.disableProfileSync(profile.profileId)
                : await profilesService.enableProfileSync(profile.profileId);
            dispatch(profilesActions.updateProfile(updatedProfile));
            dispatch(alertActions.success(t('alertMessages.editSuccess')));
        } catch (error) {
            handleApiError(error, 'alertMessages.editFail');
        }
    };

    const onResolve = e => {
        e.stopPropagation();
        handleCloseTop();
        onClickResolve(anchor.id);
    };

    const onVerify = e => {
        e.stopPropagation();
        handleCloseTop();
        const profile = findProfileById(data, anchor.id);
        setVerificationModal({
            open: true,
            profileId: profile?.profileId,
            googleAccountId: profile?.googleAccountId,
        });
    };

    const onReconnect = e => {
        e.stopPropagation();
        handleCloseTop();
        navigate(RoutePath.AccountSettingsIntegrations);
    };

    const onScannerAction = async e => {
        e.stopPropagation();
        handleCloseTop();
        const profile = findProfileById(data, anchor.id);
        if (!profile) return;

        if (!isLegacyPlan) {
            setAddOnModal({
                open: true,
                location: {
                    id: profile.locationId,
                    name: locations?.find(item => item.companyId === profile.locationId)?.internalName ?? '',
                },
            });
            return;
        }

        if (!profile.canEdit) return;

        dispatch(profilesActions.markLocationPublishing(profile.locationId));

        try {
            await companyService.connectListingDirectories(profile.locationId);
            await dispatch(accountActions.get());
            if (typeof onRefetch === 'function') {
                await onRefetch();
            }
            dispatch(alertActions.success(t('SettingsCompanies.locationPublishedMsg')));
        } catch (error) {
            // Roll back the optimistic flip by refetching authoritative state.
            if (typeof onRefetch === 'function') {
                onRefetch();
            }
            handleApiError(error);
        }
    };

    const getMenuForRow = useCallback(
        rowProfileId => {
            const profile = findProfileById(data, rowProfileId);
            const isVerified = profile?.isVerified;
            const isListingDirectory = profile?.platform === ListingDirectoryPlatformId;
            const canEdit = profile?.canEdit;
            const scannerProfile = isScannerProfile(profile);

            const openProfileItem = profile?.businessUrl
                ? [
                      {
                          id: 1,
                          icon: <OpenProfileIcon />,
                          title: t('ProfilesPage.openProfile'),
                          onClick: () => window.open(profile?.businessUrl, '_blank'),
                      },
                  ]
                : [];

            if (scannerProfile) {
                const showScannerAction = canShowScannerAction({
                    profile,
                    isLegacyPlan,
                    canManageSettings,
                    allowDirectories,
                });
                return [
                    ...openProfileItem,
                    ...(showScannerAction
                        ? [
                              {
                                  id: 2,
                                  icon: isLegacyPlan ? (
                                      <LanguageIcon
                                          sx={{ height: '20px', width: '20px', fill: 'var(--text-secondary)' }}
                                      />
                                  ) : (
                                      <UpgradeIcon />
                                  ),
                                  title: isLegacyPlan ? t('ProfilesPage.publishAction') : t('buttons.upgrade'),
                                  onClick: onScannerAction,
                              },
                          ]
                        : []),
                ];
            }

            return [
                ...openProfileItem,
                ...(profile?.syncStatus === SYNC_TYPE.DISCONNECTED && canManageSettings
                    ? [
                          {
                              id: 4,
                              icon: <LinkIcon sx={{ height: '20px', width: '20px', fill: 'var(--text-secondary)' }} />,
                              title: t('buttons.reconnect'),
                              onClick: onReconnect,
                          },
                      ]
                    : []),
                ...(canEdit &&
                (profile?.syncStatus === SYNC_TYPE.ACTION_REQUIRED ||
                    profile?.syncStatus === SYNC_TYPE.SYNCED_WITH_CONFLICTS)
                    ? [
                          isVerified
                              ? {
                                    id: 2,
                                    icon: <StyledSuccessIcon />,
                                    title: t('ProfilesPage.resolveTitle'),
                                    onClick: onResolve,
                                }
                              : {
                                    id: 2,
                                    icon: <StyledSuccessIcon />,
                                    title: t('common.verify'),
                                    onClick: onVerify,
                                },
                      ]
                    : []),
                ...(!isListingDirectory && canEdit && profile?.syncStatus !== SYNC_TYPE.DISCONNECTED
                    ? [
                          {
                              id: 3,
                              icon: profile?.syncEnabled ? (
                                  <NotificationsOffIcon
                                      sx={{ height: '20px', width: '20px', fill: 'var(--text-secondary)' }}
                                  />
                              ) : (
                                  <NotificationsIcon
                                      sx={{ height: '20px', width: '20px', fill: 'var(--text-secondary)' }}
                                  />
                              ),
                              title: profile?.syncEnabled
                                  ? t('Listings.disableProfileSync')
                                  : t('Listings.enableProfileSync'),
                              onClick: onToggleEnableSync,
                          },
                      ]
                    : []),
            ];
        },
        [
            data,
            t,
            isLegacyPlan,
            canManageSettings,
            allowDirectories,
            onRefetch,
            onResolve,
            onVerify,
            onReconnect,
            onToggleEnableSync,
            onScannerAction,
        ],
    );

    return (
        <>
            <VerificationModal
                open={verificationModal.open}
                onClose={() => setVerificationModal({ open: false, profileId: null, googleAccountId: null })}
                onVerified={onRefetch}
                profileId={verificationModal.profileId}
                googleAccountId={verificationModal.googleAccountId}
            />
            {addOnModal.open ? (
                <DirectoriesAddOnModal
                    isOpen={addOnModal.open}
                    location={addOnModal.location}
                    onClose={() => setAddOnModal({ open: false, location: null })}
                    onEnrolled={onRefetch}
                />
            ) : null}
            <TableContainer>
                <Table
                    withHover
                    columnNames={getTableHeader(t)}
                    rows={getTableBody(
                        data,
                        locations,
                        language,
                        anchor,
                        handleOpenTop,
                        handleCloseTop,
                        getMenuForRow,
                        isLegacyPlan,
                        canManageSettings,
                        allowDirectories,
                        t,
                    )}
                    isDataLoading={loading}
                    withPagination
                    paginationProps={{
                        colSpan: 7,
                        count: totalCount,
                        rowsPerPage: rowsPerPage,
                        page: page,
                        onPageChange,
                        onRowsPerPageChange,
                        ...getPaginationLabels(t),
                        pages,
                    }}
                />
            </TableContainer>
        </>
    );
};

export default ProfilesTable;
